import { dayJs } from './dayjs'
import { useGlobalI18n } from './global-i18n'
import { getFormattedDateRange, hasTimeComponent, joinRange } from './get-formatted-date-range'

interface Recurrence {
  dateTimeStart?: string | null
  dateTimeEnd?: string | null
}

// Every recurrence of a programme gets shown, but a series that runs at the
// same time of day on several days differs only in its day number, so those
// collapse into a single label ("3, 5, 7 juni 2026, 20.00–22.00"). A recurrence
// that spans more than one day, or that starts at a different time than its
// neighbours, keeps a label of its own -- collapsing it would hide exactly the
// part that makes it different.
export const getFormattedRecurrences = (recurrences?: Recurrence[] | null): string[] => {
  const dayjs = dayJs()
  const { locale } = useGlobalI18n()

  const entries = (recurrences ?? [])
    .filter((recurrence): recurrence is { dateTimeStart: string, dateTimeEnd: string } => (
      Boolean(recurrence?.dateTimeStart && recurrence?.dateTimeEnd)
    ))
    .map((recurrence) => {
      const start = dayjs(recurrence.dateTimeStart).tz('Europe/Amsterdam')
      const end = dayjs(recurrence.dateTimeEnd).tz('Europe/Amsterdam')
      const hasTime = hasTimeComponent(recurrence.dateTimeStart, recurrence.dateTimeEnd)

      return {
        recurrence,
        start,
        end,
        hasTime,
        // Recurrences collapse together only when this key matches. A multi-day
        // recurrence gets a null key, which never matches -- not even another
        // null -- so it always stands alone.
        key: start.isSame(end, 'day')
          ? (hasTime ? `${start.format('HH:mm')}–${end.format('HH:mm')}` : 'all-day')
          : null,
      }
    })

  type Entry = typeof entries[number]

  // Only consecutive recurrences are merged: the source order is the order the
  // dates were entered, and reordering it to gather far-apart matches would
  // scramble the sequence a reader follows down the list.
  const runs: Entry[][] = []
  for (const entry of entries) {
    const run = runs[runs.length - 1]
    if (run && run[0]!.key !== null && run[0]!.key === entry.key) run.push(entry)
    else runs.push([entry])
  }

  const collapsedLabel = (run: Entry[]) => {
    const { hasTime, start: firstStart, end: firstEnd } = run[0]!

    // House style, as in getFormattedDateRange: English sets the year off with
    // a comma, except when a time follows and would need that comma itself.
    const yearFmt = locale.value === 'en' && !hasTime ? ', YYYY' : ' YYYY'

    // Days sharing a month are listed under it once ("3, 5 juni"). The year is
    // only spelled out on the last group of each year, so a run inside one year
    // ends on a single year.
    const monthGroups: Entry[][] = []
    for (const entry of run) {
      const group = monthGroups[monthGroups.length - 1]
      if (group && group[0]!.start.isSame(entry.start, 'month')) group.push(entry)
      else monthGroups.push([entry])
    }

    const dates = monthGroups
      .map((group, index) => {
        const next = monthGroups[index + 1]
        const showYear = !next || !next[0]!.start.isSame(group[0]!.start, 'year')
        const days = group.map(entry => entry.start.format('D')).join(', ')
        return `${days} ${group[0]!.start.format(`MMMM${showYear ? yearFmt : ''}`)}`
      })
      .join(', ')

    if (!hasTime) return dates

    const timeFmt = locale.value === 'en' ? 'HH:mm' : 'HH.mm'
    return joinRange(`${dates}, ${firstStart.format(timeFmt)}`, firstEnd.format(timeFmt))
  }

  return runs
    .map(run => (
      run.length === 1
        ? getFormattedDateRange(run[0]!.recurrence.dateTimeStart, run[0]!.recurrence.dateTimeEnd)
        : collapsedLabel(run)
    ))
    .filter(Boolean)
}
